import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

// Generic auto-save settings that apply to any SaaS app
export interface AutoSaveSettings {
  enabled: boolean;
  intervalSeconds: number;
}

// Generic user settings - apps can extend this interface
export interface UserSettings {
  autoSave: AutoSaveSettings;
}

interface SettingsContextType {
  settings: UserSettings;
  updateAutoSave: (settings: Partial<AutoSaveSettings>) => void;
  updateSettings: (settings: Partial<UserSettings>) => void;
  resetToDefaults: () => void;
}

const STORAGE_KEY = "app-settings";

const DEFAULT_SETTINGS: UserSettings = {
  autoSave: {
    enabled: true,
    intervalSeconds: 30,
  },
};

const SettingsContext = createContext<SettingsContextType | null>(null);

function getStoredSettings(key: string, defaults: UserSettings): UserSettings {
  if (typeof window === "undefined") return defaults;
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        autoSave: { ...defaults.autoSave, ...parsed.autoSave },
      };
    }
  } catch {
    // Invalid JSON, use defaults
  }
  return defaults;
}

interface SettingsProviderProps {
  children: ReactNode;
  storageKey?: string;
  defaultSettings?: Partial<UserSettings>;
}

export function SettingsProvider({ children, storageKey, defaultSettings }: SettingsProviderProps) {
  const effectiveStorageKey = storageKey || STORAGE_KEY;
  const effectiveDefaultsRef = useRef({ ...DEFAULT_SETTINGS, ...defaultSettings });

  const [settings, setSettings] = useState<UserSettings>(() =>
    getStoredSettings(effectiveStorageKey, effectiveDefaultsRef.current),
  );

  useEffect(() => {
    localStorage.setItem(effectiveStorageKey, JSON.stringify(settings));
  }, [settings, effectiveStorageKey]);

  const updateAutoSave = useCallback((updates: Partial<AutoSaveSettings>) => {
    setSettings((prev) => ({
      ...prev,
      autoSave: { ...prev.autoSave, ...updates },
    }));
  }, []);

  const updateSettings = useCallback((updates: Partial<UserSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setSettings(effectiveDefaultsRef.current);
  }, []);

  const value = useMemo<SettingsContextType>(
    () => ({
      settings,
      updateAutoSave,
      updateSettings,
      resetToDefaults,
    }),
    [settings, updateAutoSave, updateSettings, resetToDefaults],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
